// Historical model screens (stage 4): grouping the run log, wording, the rules form's checks and the "re-run
// history?" prompt. Pure functions only; the components call these and render.
// See docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, § Stage 4 design (decisions 3, 5, 7 and the settled wording rule).

import { DEFAULT_RUN_CONFIG, capToMaxRange, describeDurationText, parseDuration, splitWindow, toIso } from "./historicalRuns";
import type { HistoricalRunConfig, RunRequestRow, RunTrigger, TimeWindow } from "./historicalRuns";

// ── Rows as the screens read them ────────────────────────────────────────────────────────────────

/** A ProcessedModelRunRequest row (every field the Runs tab selects). */
export interface RequestRow extends RunRequestRow {
  processedModelSchemaId?: string;
  chunkCount?: number | null;
  mergedInto?: string | null;
  error?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  owner?: string | null;
  organizationId?: string | null;
  updatedAt?: string;
}

/** A ProcessedModelRunHistory row of a historical run (one per attempt). */
export interface RunRow {
  id: string;
  processedModelSchemaId?: string;
  requestId?: string | null;
  status: "PROCESSING" | "COMPLETE" | "FAILED";
  description?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  executionTimeMs?: number | null;
  attempt?: number | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  triggerType?: RunTrigger | null;
  outputS3Keys?: string | null;
  inputs?: string | null;
}

export type RowStatus = "waiting" | "running" | "succeeded" | "failed" | "cancelled" | "combined";

/** One line of the Runs tab: a request that did, or will do, work (decision 3). */
export interface RunGroup {
  id: string;
  request: RequestRow;
  status: RowStatus;
  /** The range the row covers; null for a Run now / scheduled request that hasn't started. */
  window: TimeWindow | null;
  /** The attempts (run rows) of this request, oldest first. */
  attempts: RunRow[];
  /** Requests folded into this one (MERGED with mergedInto = this id), newest first. */
  combined: RequestRow[];
  /** A SPLIT request's pieces, in order. Empty for a single request. */
  chunks: RunGroup[];
  /** The pieces by status, e.g. {succeeded: 23, running: 1, waiting: 1}. Empty for a single request. */
  counts: Partial<Record<RowStatus, number>>;
  /** The error of a failed request (its own, or its first failed piece's). */
  error: string | null;
}

export type RunsFilter = "all" | "failed" | "waiting";

const IN_FLIGHT: RowStatus[] = ["waiting", "running"];

function requestStatus(request: RequestRow): RowStatus {
  switch (request.status) {
    case "QUEUED":
      return "waiting";
    case "RUNNING":
      return "running";
    case "SUCCEEDED":
      return "succeeded";
    case "FAILED":
      return "failed";
    case "CANCELLED":
      return "cancelled";
    case "MERGED":
      return "combined";
    case "SPLIT":
      return "running"; // refined from the chunks in groupRequests
    default:
      return "waiting";
  }
}

function windowOf(request: RequestRow): TimeWindow | null {
  return request.windowStart && request.windowEnd ? { start: request.windowStart, end: request.windowEnd } : null;
}

function byCreatedDesc(a: { createdAt: string }, b: { createdAt: string }): number {
  return b.createdAt.localeCompare(a.createdAt);
}

function attemptsOf(request: RequestRow, runsByRequest: Map<string, RunRow[]>): RunRow[] {
  return runsByRequest.get(request.id) ?? [];
}

/** A SPLIT request's status from its pieces: done when every piece is, running while any piece has started. */
function splitStatus(chunks: RunGroup[]): RowStatus {
  if (!chunks.length) return "waiting";
  if (chunks.some((c) => IN_FLIGHT.includes(c.status))) {
    return chunks.some((c) => c.status !== "waiting") ? "running" : "waiting";
  }
  return chunks.some((c) => c.status === "failed" || c.status === "cancelled") ? "failed" : "succeeded";
}

/**
 * Fold the request rows into what a person calls one piece of work (decision 3): a SPLIT request is one row with
 * its pieces inside, MERGED requests sit under the request that absorbed them, each row carries its attempts.
 * Newest first. With `flat`, every request is its own row (the "Show every request" switch).
 */
export function groupRequests(requests: RequestRow[], runs: RunRow[], options: { flat?: boolean } = {}): RunGroup[] {
  const runsByRequest = new Map<string, RunRow[]>();
  for (const run of [...runs].sort((a, b) => (a.startedAt ?? a.id).localeCompare(b.startedAt ?? b.id))) {
    if (!run.requestId) continue;
    runsByRequest.set(run.requestId, [...(runsByRequest.get(run.requestId) ?? []), run]);
  }
  const sorted = [...requests].sort(byCreatedDesc);
  const byId = new Map(sorted.map((r) => [r.id, r]));

  const single = (request: RequestRow): RunGroup => {
    const attempts = attemptsOf(request, runsByRequest);
    const status = requestStatus(request);
    return {
      id: request.id,
      request,
      status,
      window: windowOf(request),
      attempts,
      combined: [],
      chunks: [],
      counts: {},
      error: status === "failed" ? request.error ?? attempts[attempts.length - 1]?.description ?? null : null,
    };
  };

  if (options.flat) return sorted.map(single);

  const chunksByParent = new Map<string, RequestRow[]>();
  const mergedByTarget = new Map<string, RequestRow[]>();
  const topLevel: RequestRow[] = [];
  for (const request of sorted) {
    if (request.backfillId && byId.has(request.backfillId)) {
      chunksByParent.set(request.backfillId, [...(chunksByParent.get(request.backfillId) ?? []), request]);
    } else if (request.status === "MERGED" && request.mergedInto && byId.has(request.mergedInto)) {
      mergedByTarget.set(request.mergedInto, [...(mergedByTarget.get(request.mergedInto) ?? []), request]);
    } else {
      topLevel.push(request);
    }
  }

  return topLevel.map((request) => {
    const group = single(request);
    group.combined = mergedByTarget.get(request.id) ?? [];
    const pieces = chunksByParent.get(request.id);
    if (request.status === "SPLIT" || pieces?.length) {
      group.chunks = (pieces ?? [])
        .sort((a, b) => (a.chunkIndex ?? 0) - (b.chunkIndex ?? 0))
        .map((piece) => {
          const chunk = single(piece);
          chunk.combined = mergedByTarget.get(piece.id) ?? [];
          return chunk;
        });
      group.counts = group.chunks.reduce<Partial<Record<RowStatus, number>>>((acc, chunk) => {
        acc[chunk.status] = (acc[chunk.status] ?? 0) + 1;
        return acc;
      }, {});
      group.status = request.status === "SPLIT" ? splitStatus(group.chunks) : group.status;
      if (group.status === "failed" && !group.error) {
        group.error = group.chunks.find((c) => c.status === "failed")?.error ?? null;
      }
    }
    return group;
  });
}

/** The rows a filter keeps: "failed" = failed (or a backfill with a failed piece); "waiting" = waiting or running. */
export function filterGroups(groups: RunGroup[], filter: RunsFilter): RunGroup[] {
  if (filter === "failed") return groups.filter((g) => g.status === "failed" || (g.counts.failed ?? 0) > 0);
  if (filter === "waiting") return groups.filter((g) => IN_FLIGHT.includes(g.status) || (g.counts.waiting ?? 0) > 0 || (g.counts.running ?? 0) > 0);
  return groups;
}

// ── Wording ──────────────────────────────────────────────────────────────────────────────────────

const STATUS_WORDS: Record<RowStatus, string> = {
  waiting: "waiting",
  running: "running",
  succeeded: "succeeded",
  failed: "failed",
  cancelled: "cancelled",
  combined: "combined",
};

export function statusWord(status: RowStatus): string {
  return STATUS_WORDS[status];
}

/** "25 monthly runs · 23 succeeded, 1 running, 1 waiting" or "25 monthly runs · all succeeded". */
export function describePieces(group: RunGroup, unit = "monthly"): string | null {
  if (!group.chunks.length) return null;
  const total = group.chunks.length;
  const head = `${total} ${unit} ${total === 1 ? "run" : "runs"}`;
  const order: RowStatus[] = ["succeeded", "failed", "cancelled", "running", "waiting"];
  const parts = order.filter((s) => group.counts[s]).map((s) => `${group.counts[s]} ${STATUS_WORDS[s]}`);
  if (parts.length === 1 && group.counts.succeeded === total) return `${head} · all succeeded`;
  return `${head} · ${parts.join(", ")}`;
}

/** The unit word for a template's maxWindow: P1M → "monthly", P7D → "weekly", P1D → "daily", else "". */
export function pieceUnit(config: HistoricalRunConfig | null | undefined): string {
  const text = config?.maxWindow ?? "P1M";
  try {
    const { months, ms } = parseDuration(text);
    if (months === 1 && !ms) return "monthly";
    if (!months && ms === 7 * 86_400_000) return "weekly";
    if (!months && ms === 86_400_000) return "daily";
  } catch {
    /* fall through */
  }
  return "";
}

/**
 * What started a request, in words: "Scheduled", "Run now · by Jane", "Linked to dataset", "Backfill",
 * "Backfill · piece 7 of 25", "Dataset upload · 30 days combined", "Input file replaced".
 */
export function describeTrigger(
  request: Pick<RequestRow, "triggerType" | "triggerRef" | "backfillId" | "chunkIndex" | "chunkCount">,
  options: { requesterName?: string | null; combined?: number } = {}
): string {
  const { requesterName, combined = 0 } = options;
  if (request.backfillId) {
    // chunkIndex counts from 1 (chunkId). A piece keeps its parent's trigger: a long scheduled or upload-driven
    // range is split the same way as a backfill
    const index = request.chunkIndex ?? 1;
    const kind =
      request.triggerType === "CRON"
        ? "Scheduled"
        : request.triggerType === "DATASET_UPLOAD"
          ? "Dataset upload"
          : request.triggerType === "MODEL_OUTPUT"
            ? "Model output updated"
            : request.triggerType === "INPUT_FILE"
              ? "Input file replaced"
              : "Backfill";
    return request.chunkCount ? `${kind} · piece ${index} of ${request.chunkCount}` : `${kind} · piece ${index}`;
  }
  switch (request.triggerType) {
    case "CRON":
      return "Scheduled";
    case "MANUAL":
      return requesterName ? `Run now · by ${requesterName}` : "Run now";
    case "BACKFILL":
      return request.triggerRef?.startsWith("link:")
        ? "Linked to dataset"
        : request.triggerRef?.startsWith("outlink:")
          ? "Linked to model output"
          : requesterName
            ? `Backfill · by ${requesterName}`
            : "Backfill";
    case "DATASET_UPLOAD":
      return combined > 0 ? `Dataset upload · ${combined + 1} days combined` : "Dataset upload";
    case "MODEL_OUTPUT":
      return combined > 0 ? `Model output updated · ${combined + 1} days combined` : "Model output updated";
    case "INPUT_FILE":
      return "Input file replaced";
    default:
      return String(request.triggerType ?? "Run");
  }
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const pad = (n: number) => String(n).padStart(2, "0");

/** "14 Oct 22:00" (UTC), with the year when it isn't the given year. */
export function shortUtc(iso: string, year?: number): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const base = `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  const time = `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  const withYear = year !== undefined && date.getUTCFullYear() !== year ? `${base} ${date.getUTCFullYear()}` : base;
  return `${withYear} ${time}`;
}

/** "2024-10-01 00:00" (UTC). */
export function dayTimeUtc(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

/** "2024-10-01" (UTC). */
export function dayUtc(iso: string): string {
  return dayTimeUtc(iso).slice(0, 10);
}

/** "just now", "5 min ago", "2 h ago", "3 days ago". */
export function ago(iso: string, now: string): string {
  const ms = Date.parse(now) - Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

/** "4 min", "6 s", "1 h 12 min". */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

/**
 * The card's status line for a HISTORICAL model (the wording rule: never "issued" or "forecast"):
 * "Data to 14 Oct 22:00 · last run succeeded 2 h ago", "No data yet · last run failed 5 min ago",
 * "Paused · uploads don't start runs · Data to …".
 */
export function describeHistoricalStatus(
  summary: { dataEnd?: string | null; lastRun?: { status: RowStatus; at: string } | null; paused?: boolean; writesDataset?: boolean },
  now: string
): string {
  const parts: string[] = [];
  if (summary.paused) parts.push("Paused · uploads don't start runs");
  // A data runner's rows live in its dataset, not in a series of its own
  parts.push(
    summary.dataEnd
      ? `Data to ${shortUtc(summary.dataEnd, new Date(now).getUTCFullYear())}`
      : summary.writesDataset
        ? "Writes to a dataset"
        : "No data yet"
  );
  if (summary.lastRun) parts.push(`last run ${STATUS_WORDS[summary.lastRun.status]} ${ago(summary.lastRun.at, now)}`);
  else parts.push("no runs yet");
  return parts.join(" · ");
}

/** The most recent request that did work, as the card's "last run". */
export function lastRunOf(groups: RunGroup[]): { status: RowStatus; at: string } | null {
  const latest = groups.find((g) => g.status !== "combined");
  if (!latest) return null;
  return { status: latest.status, at: latest.request.completedAt ?? latest.request.startedAt ?? latest.request.createdAt };
}

// ── The rules form (decision 5) ──────────────────────────────────────────────────────────────────

/** The quality-rules processor's vocabulary (stage 3, decisions 6–7): one entry per column. */
export interface ColumnRules {
  sentinels?: number[];
  belowMin?: { min: number; set: number };
  clamp?: { min: number; max: number };
  fillGaps?: { maxGap: string };
}

export type RulesByColumn = Record<string, ColumnRules>;

export const RULE_KEYS = ["sentinels", "belowMin", "clamp", "fillGaps"] as const;

export interface RuleError {
  column: string;
  /** The rule the error is about; undefined when the column itself is the problem. */
  key?: string;
  message: string;
}

const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Every problem with a rules object for the ticked columns; empty when it's fine (an empty object cleans nothing). */
export function rulesErrors(rules: unknown, columns: string[]): RuleError[] {
  const errors: RuleError[] = [];
  if (rules === null || rules === undefined) return errors;
  if (!isPlainObject(rules)) return [{ column: "", message: "Rules must be an object with one entry per column" }];
  for (const [column, value] of Object.entries(rules)) {
    // The wizard marks the object's type beside its entries ({ …, type: "dict" }); the processor skips it too
    if (column === "type" && typeof value === "string") continue;
    if (!columns.includes(column)) {
      errors.push({ column, message: `${column} is not a ticked column` });
      continue;
    }
    if (!isPlainObject(value)) {
      errors.push({ column, message: `${column}: rules must be an object` });
      continue;
    }
    for (const [key, rule] of Object.entries(value)) {
      if (!(RULE_KEYS as readonly string[]).includes(key)) {
        errors.push({ column, key, message: `${column}: "${key}" is not a rule (sentinels, belowMin, clamp, fillGaps)` });
        continue;
      }
      if (key === "sentinels") {
        if (!Array.isArray(rule) || !rule.every(isNumber)) errors.push({ column, key, message: `${column}: sentinels must be numbers only` });
      } else if (key === "belowMin") {
        if (!isPlainObject(rule) || !isNumber(rule.min) || !isNumber(rule.set)) {
          errors.push({ column, key, message: `${column}: belowMin needs a number min and a number set` });
        }
      } else if (key === "clamp") {
        if (!isPlainObject(rule) || !isNumber(rule.min) || !isNumber(rule.max)) {
          errors.push({ column, key, message: `${column}: clamp needs a number min and a number max` });
        } else if (rule.max < rule.min) {
          errors.push({ column, key, message: `${column}: clamp max must not be below min` });
        }
      } else if (key === "fillGaps") {
        if (!isPlainObject(rule) || typeof rule.maxGap !== "string") {
          errors.push({ column, key, message: `${column}: fillGaps needs a maxGap duration` });
        } else {
          try {
            const { months, ms } = parseDuration(rule.maxGap);
            if (months || ms <= 0) errors.push({ column, key, message: `${column}: fillGaps.maxGap must be a positive duration of days or less` });
          } catch {
            errors.push({ column, key, message: `${column}: fillGaps.maxGap "${rule.maxGap}" is not an ISO 8601 duration` });
          }
        }
      }
    }
  }
  return errors;
}

/**
 * The template's default rules for a column: the field's defaultValue keyed by column name, else its "*" entry, else
 * (when the defaultValue is itself one rule block) that block, else nothing.
 */
export function defaultRulesFor(defaultValue: unknown, column: string): ColumnRules {
  if (!isPlainObject(defaultValue)) return {};
  if (isPlainObject(defaultValue[column])) return { ...(defaultValue[column] as ColumnRules) };
  if (isPlainObject(defaultValue["*"])) return { ...(defaultValue["*"] as ColumnRules) };
  if (Object.keys(defaultValue).every((k) => (RULE_KEYS as readonly string[]).includes(k))) return { ...(defaultValue as ColumnRules) };
  return {};
}

/** The rules object for the ticked columns: existing entries kept, new columns from the defaults, others dropped. */
export function rulesForColumns(current: unknown, columns: string[], defaultValue: unknown): RulesByColumn {
  const existing = isPlainObject(current) ? current : {};
  const next: RulesByColumn = {};
  for (const column of columns) {
    next[column] = isPlainObject(existing[column]) ? (existing[column] as ColumnRules) : defaultRulesFor(defaultValue, column);
  }
  return next;
}

// ── Durations as a number and a unit (the form's boxes) ──────────────────────────────────────────

export type DurationUnit = "minutes" | "hours" | "days" | "months" | "years";

const UNIT_MS: Record<Exclude<DurationUnit, "months" | "years">, number> = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 };

/** "PT6H" → {value: 6, unit: "hours"}; "P1M" → {1, months}; "P5Y" → {5, years}; null when the text isn't a duration. */
export function durationParts(text: string | null | undefined): { value: number; unit: DurationUnit } | null {
  if (!text) return null;
  try {
    const { months, ms } = parseDuration(text);
    if (months && !ms && months % 12 === 0) return { value: months / 12, unit: "years" };
    if (months && !ms) return { value: months, unit: "months" };
    if (months) return null;
    for (const unit of ["days", "hours", "minutes"] as const) {
      if (ms % UNIT_MS[unit] === 0) return { value: ms / UNIT_MS[unit], unit };
    }
    return null;
  } catch {
    return null;
  }
}

/** {6, hours} → "PT6H". */
export function durationText(value: number, unit: DurationUnit): string {
  if (!Number.isFinite(value) || value < 0) return "";
  const n = Math.round(value);
  switch (unit) {
    case "years":
      return `P${n}Y`;
    case "months":
      return `P${n}M`;
    case "days":
      return `P${n}D`;
    case "hours":
      return `PT${n}H`;
    case "minutes":
      return `PT${n}M`;
    default:
      return "";
  }
}

/** "6 hours", "1 month", "30 s" for the Model Garden and the wizard's summaries. */
export function describeDuration(text: string | null | undefined): string {
  const parts = durationParts(text);
  if (!parts) return text ?? "";
  const unit = parts.value === 1 ? parts.unit.replace(/s$/, "") : parts.unit;
  return `${parts.value} ${unit}`;
}

// ── The "re-run history?" prompt (decision 7) ───────────────────────────────────────────────────

export interface RerunPrompt {
  window: TimeWindow;
  chunks: number;
  /** The start was pulled forward to maxRequestRange (or earliestStart): the prompt says "the last 5 years" */
  capped: boolean;
  /** Why the history can't be queued as one request (more pieces than the run function accepts): no button */
  problem?: string;
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(value[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

/**
 * A setting as the processor reads it: the wizard saves `{value, type}` wrappers and a `type` key on objects
 * (injectTypesToSchemaFields), the harness stores raw values; the processor unwraps and skips `type`, so the two
 * must compare equal. Exported for its tests.
 */
export function normalizeSetting(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeSetting);
  if (!isPlainObject(value)) return value;
  const keys = Object.keys(value);
  if (keys.includes("value") && keys.every((k) => k === "value" || k === "type")) return normalizeSetting(value.value);
  const out: Record<string, unknown> = {};
  for (const k of keys) if (k !== "type") out[k] = normalizeSetting(value[k]);
  return out;
}

/**
 * Whether saving a model's edited settings should offer to re-run its history: only when a setting other than a
 * dataset field changed (dataset changes backfill by themselves, stage 3 decision 4), and only when there is history
 * to re-run (the output's dataStart, else the model's backfillFrom). Returns the range and how many runs it takes.
 */
export function rerunPromptFor(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  context: { datasetFieldKeys: string[]; dataStart?: string | null; backfillFrom?: string | null; now: string; config: HistoricalRunConfig }
): RerunPrompt | null {
  const strip = (fields: Record<string, unknown> | null | undefined) => {
    const copy: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(fields ?? {})) {
      if (!context.datasetFieldKeys.includes(key)) copy[key] = normalizeSetting(value);
    }
    return copy;
  };
  if (stableJson(strip(before)) === stableJson(strip(after))) return null;
  const start = context.dataStart ?? context.backfillFrom;
  if (!start || Number.isNaN(Date.parse(start)) || Date.parse(start) >= Date.parse(context.now)) return null;
  // Never longer than the run function accepts (checkRange refuses a request past maxRequestRange)
  const cappedStart = capToMaxRange(start, context.now, context.config);
  const window = { start: cappedStart, end: toIso(context.now) };
  const capped = Date.parse(cappedStart) > Date.parse(start);
  if (Date.parse(window.start) >= Date.parse(window.end)) return null;
  try {
    return { window, chunks: splitWindow(window, context.config).length, capped };
  } catch (err) {
    // The run function would refuse the same request (review 2026-10-17: the prompt offered it anyway)
    return { window, chunks: 0, capped, problem: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * The text of the prompt: "Re-run history with the new settings? 2024-10-01 → today, 25 monthly runs. …", or
 * "… the last 5 years → today …" when the history is longer than the template allows in one request.
 */
export function rerunPromptText(prompt: RerunPrompt, config: HistoricalRunConfig | null | undefined): string {
  const from = prompt.capped ? `the last ${describeDurationText(config?.maxRequestRange || "P5Y")} (${dayUtc(prompt.window.start)})` : dayUtc(prompt.window.start);
  if (prompt.problem) {
    return `The past keeps the old settings: ${from} → today can't be re-run as one request (${prompt.problem}). Re-run a range from the model's page instead.`;
  }
  return `Re-run history with the new settings? ${from} → today${runsText(prompt.chunks, config)}. Until then the past keeps the old settings.`;
}

/** ", 25 monthly runs" / ", 1 monthly run" / "" when the count is unknown. */
function runsText(chunks: number, config: HistoricalRunConfig | null | undefined): string {
  if (!chunks) return "";
  const unit = pieceUnit(config);
  return `, ${chunks} ${unit ? `${unit} ` : ""}${chunks === 1 ? "run" : "runs"}`;
}

// ── The wizard's owner and dataset choices (pre-rollout review 2026-10-17, decision R2 A) ───────

/**
 * Who owns an organization model the wizard creates: an Admin keeps the convention forecast models use (the
 * organization's owner); anyone else owns what they create. AppSync's owner rule only lets a caller create a row
 * whose owner is themselves, so a member stamping the organization owner's id was refused at the wizard's last step.
 * An unknown `isAdmin` (the chat deploy card, used by Admins) keeps the convention.
 */
export function organizationModelOwner(args: { isAdmin?: boolean; organizationOwner?: string | null; selfId?: string | null }): string {
  const { isAdmin, organizationOwner, selfId } = args;
  if (isAdmin === false) return selfId || "";
  return organizationOwner || "";
}

/**
 * The dataset picker's verdict for one dataset under the wizard's owner choice: the run function's crossOrg rule
 * (`canModelReadDataset`, stage 3 decision 5), so the picker refuses what the function would refuse and only warns
 * where an Admin owner is let through. A personal model reads its owner's own datasets; an organization model its
 * own organization's; anything else is crossOrg: allowed for an Admin owner, refused for everyone else. An unknown
 * `isAdmin` only warns (as organizationModelOwner keeps the Admin convention for it): the run function decides.
 */
export function datasetPickVerdict(args: {
  ownerType?: "personal" | "organization";
  selectedOrgKey?: string;
  isAdmin?: boolean;
  datasetOrganizationId?: string | null;
}): { crossOrg: boolean; refused: string | null } {
  const { ownerType, selectedOrgKey, isAdmin, datasetOrganizationId } = args;
  const datasetOrg = datasetOrganizationId || null;
  const crossOrg = ownerType === "organization" && selectedOrgKey ? datasetOrg !== selectedOrgKey : Boolean(datasetOrg);
  if (!crossOrg || isAdmin !== false) return { crossOrg, refused: null };
  return {
    crossOrg,
    refused:
      ownerType === "organization"
        ? "it isn't in the model's organization; only an Admin can link another organization's dataset"
        : "a personal model can only read your own datasets",
  };
}

// ── The wizard's history and trigger settings (stage 4, decisions 4 and 7) ───────────────────────

export const ALL_TRIGGERS: RunTrigger[] = ["MANUAL", "BACKFILL", "DATASET_UPLOAD", "MODEL_OUTPUT", "CRON", "INPUT_FILE"];

/** The triggers a template allows; an empty or missing list means every trigger. */
export function allowedTriggersOf(config: HistoricalRunConfig | null | undefined): RunTrigger[] {
  return config?.allowedTriggers?.length ? (config.allowedTriggers as RunTrigger[]) : ALL_TRIGGERS;
}

export interface HistoryFormValues {
  /** "Process history" ticked */
  processHistory: boolean;
  /** The optional From day, "YYYY-MM-DD" */
  backfillFrom?: string | null;
}

/**
 * The model's historicalSettings from the wizard's two history fields, over whatever else it already stored (a
 * seeded overlap, say), which an edit must not drop.
 */
export function historicalSettingsFromForm(stored: Record<string, unknown> | null | undefined, form: HistoryFormValues): Record<string, unknown> {
  const { backfillFrom: _b, autoBackfill: _a, ...rest } = stored || {};
  return {
    ...rest,
    ...(form.backfillFrom ? { backfillFrom: `${form.backfillFrom}T00:00:00.000Z` } : {}),
    ...(form.processHistory ? {} : { autoBackfill: false }),
  };
}

/**
 * The History step's preview: "2026-05-01 → today, 6 monthly runs" (capped to maxRequestRange and earliestStart the
 * way the link backfill is), the "from the dataset's first day" wording, or why nothing runs.
 */
export function describeHistoryPlan(
  form: HistoryFormValues,
  context: { now: string; config: HistoricalRunConfig; editing?: boolean; /** "dataset" (default) or "model output": what the history is read from */ source?: string }
): string {
  const source = context.source || "dataset";
  if (!form.processHistory) return "History is not processed automatically. Use Re-run a range on the model when you want it.";
  if (!form.backfillFrom) {
    // A model with no dataset or model-output input (a data runner such as GEFS analysis) has no "first day" to start from
    if (source === "none") {
      return `No start date: each run covers the last ${describeDurationText(context.config.defaultLookback || DEFAULT_RUN_CONFIG.defaultLookback)}. Pick a date to backfill history.`;
    }
    return context.editing ? `From the ${source}'s first day to today.` : `From the ${source}'s first day to today, as soon as the model is created.`;
  }
  const requested = `${form.backfillFrom}T00:00:00.000Z`;
  if (Number.isNaN(Date.parse(requested)) || Date.parse(requested) >= Date.parse(context.now)) return "";
  const start = capToMaxRange(requested, context.now, context.config);
  if (Date.parse(start) >= Date.parse(context.now)) return "";
  const from = Date.parse(start) > Date.parse(requested) ? `the last ${describeDurationText(context.config.maxRequestRange || "P5Y")} (${dayUtc(start)})` : form.backfillFrom;
  try {
    return `${from} → today${runsText(splitWindow({ start, end: toIso(context.now) }, context.config).length, context.config)}`;
  } catch (err: any) {
    return err?.message || "";
  }
}

/**
 * The backfill a new model queues at once: [backfillFrom, now], capped to maxRequestRange and earliestStart. Only for a
 * template with no dataset or model_output input — those backfill through their links already — that allows BACKFILL,
 * with history on (autoBackfill not false) and a from-date. The run function splits it into maxWindow chunks, which
 * run oldest first ahead of any later scheduled run; the schedule then carries on from the watermark.
 */
export function startBackfillWindow(args: {
  fieldDefinitions: Record<string, { type?: string } | null | undefined> | null | undefined;
  settings: { backfillFrom?: string | null; autoBackfill?: boolean | null } | null | undefined;
  config: HistoricalRunConfig;
  now: string;
}): TimeWindow | null {
  const { fieldDefinitions, settings, config, now } = args;
  const hasInputs = Object.values(fieldDefinitions ?? {}).some((f) => f?.type === "dataset" || f?.type === "model_output");
  if (hasInputs || settings?.autoBackfill === false || !settings?.backfillFrom) return null;
  if (!allowedTriggersOf(config).includes("BACKFILL")) return null;
  if (Number.isNaN(Date.parse(settings.backfillFrom)) || Date.parse(settings.backfillFrom) >= Date.parse(now)) return null;
  const start = capToMaxRange(settings.backfillFrom, now, config);
  if (Date.parse(start) >= Date.parse(now)) return null;
  return { start, end: toIso(Date.parse(now)) };
}

// ── The datasets a run read (decision 4) ─────────────────────────────────────────────────────────

export interface RunDatasetLineage {
  fieldKey: string;
  datasetId: string;
  files: { fileId: string; version: number }[];
}

/** The lineage a run recorded in its inputs snapshot (`inputs.datasets`), or none. */
export function datasetsReadBy(run: Pick<RunRow, "inputs">): RunDatasetLineage[] {
  if (!run.inputs) return [];
  try {
    const parsed = JSON.parse(run.inputs) as { datasets?: RunDatasetLineage[] };
    return Array.isArray(parsed.datasets) ? parsed.datasets : [];
  } catch {
    return [];
  }
}

/** A `model_output` input a run read: the upstream forecast output and the issues it took (older runs may record
 * SERIES file versions from the retired model series store). */
export interface RunOutputLineage {
  fieldKey: string;
  outputParamId: string;
  modelId: string;
  storageMode: "FILES" | string;
  files: { fileId: string; version?: number; forecastIssued?: string }[];
}

/** The upstream model outputs a run recorded in its inputs snapshot (`inputs.modelOutputs`), or none. */
export function outputsReadBy(run: Pick<RunRow, "inputs">): RunOutputLineage[] {
  if (!run.inputs) return [];
  try {
    const parsed = JSON.parse(run.inputs) as { modelOutputs?: RunOutputLineage[] };
    return Array.isArray(parsed.modelOutputs) ? parsed.modelOutputs : [];
  } catch {
    return [];
  }
}

/** The settings a run saw (`inputs.schemaFields`), or null when the snapshot was dropped. */
export function settingsUsedBy(run: Pick<RunRow, "inputs">): Record<string, unknown> | null {
  if (!run.inputs) return null;
  try {
    const parsed = JSON.parse(run.inputs) as { schemaFields?: Record<string, unknown> };
    return isPlainObject(parsed.schemaFields) && !parsed.schemaFields._truncated ? parsed.schemaFields : null;
  } catch {
    return null;
  }
}
